/**
 * Escrow disclosure helpers (ADR-0013).
 *
 * A disclosure is the servicer's annual escrow analysis for one 12-month cycle
 * starting at `cycle_start` (always the 1st of a month). Dates go through
 * toUtcDay / monthIndexOfDay because the API may send ISO or RFC-1123 strings
 * (CLAUDE.md "Bill Period Math"); money arrives as decimal strings.
 */
import { monthIndexOfDay, monthName, toUtcDay } from './billCoverage';
import { roundCents } from './numericInput';

// A category total within this much of the servicer's printed total is
// rounding on the document, not a missing payee.
const CATEGORY_TOLERANCE = 1;

export const toMoney = (value) => {
  if (value == null || value === '') return null;
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(num) ? num : null;
};

/** Months since year 0 of the cycle's first month, or null when undated. */
export const cycleMonthIndex = (disclosure) => {
  const day = toUtcDay(disclosure?.cycle_start);
  return day == null ? null : monthIndexOfDay(day);
};

/** "Aug 2026 – Jul 2027". */
export const formatCycle = (cycleStart) => {
  const day = toUtcDay(cycleStart);
  if (day == null) return '—';
  const start = monthIndexOfDay(day);
  return `${monthName(start, 'short')} – ${monthName(start + 11, 'short')}`;
};

/** "August 2026": when the new escrow payment takes effect. */
export const formatCycleStart = (cycleStart) => {
  const day = toUtcDay(cycleStart);
  return day == null ? '—' : monthName(monthIndexOfDay(day), 'long');
};

export const sortDisclosuresNewestFirst = (disclosures = []) =>
  [...disclosures].sort((a, b) => {
    const am = cycleMonthIndex(a);
    const bm = cycleMonthIndex(b);
    if (am !== bm) return (bm ?? -Infinity) - (am ?? -Infinity);
    return (b.id ?? 0) - (a.id ?? 0);
  });

export const latestDisclosure = (disclosures = []) => sortDisclosuresNewestFirst(disclosures)[0] ?? null;

/**
 * One row per disclosure, newest first, with the change in the monthly escrow
 * payment. The document's own "previous payment" is the true prior figure; when
 * it wasn't read, fall back to the disclosure for the cycle immediately before,
 * but never across a missing year, where the change would span two cycles.
 */
export const escrowHistory = (disclosures = []) => {
  const sorted = sortDisclosuresNewestFirst(disclosures);
  return sorted.map((disclosure, index) => {
    const escrow = toMoney(disclosure.new_escrow_payment);
    let previous = toMoney(disclosure.previous_escrow_payment);
    if (previous == null) {
      const older = sorted[index + 1];
      const thisMonth = cycleMonthIndex(disclosure);
      if (older && thisMonth != null && cycleMonthIndex(older) === thisMonth - 12) {
        previous = toMoney(older.new_escrow_payment);
      }
    }
    return {
      disclosure,
      cycleLabel: formatCycle(disclosure.cycle_start),
      escrow,
      change: escrow != null && previous != null ? roundCents(escrow - previous) : null,
    };
  });
};

const CATEGORY_FIELDS = [
  'property_taxes_annual',
  'homeowners_insurance_annual',
  'mortgage_insurance_annual',
  'other_annual',
];

/**
 * How far the category totals fall short of (positive) or exceed (negative)
 * the servicer's printed total, or null when they agree or can't be compared.
 * A gap usually means a payee was read into no category, or a repeated
 * installment was counted once.
 */
export const categoryShortfall = (disclosure) => {
  const total = toMoney(disclosure?.total_annual_disbursements);
  const parts = CATEGORY_FIELDS.map((field) => toMoney(disclosure?.[field])).filter((v) => v != null);
  if (total == null || parts.length === 0) return null;
  const gap = roundCents(total - parts.reduce((sum, v) => sum + v, 0));
  return Math.abs(gap) > CATEGORY_TOLERANCE ? gap : null;
};

/**
 * Amortization-page defaults from a disclosure: yearly taxes and insurance as
 * printed, and mortgage insurance as a monthly figure (the page's PMI input is
 * monthly). Each is null when the disclosure didn't carry it, so the caller can
 * fall back field by field.
 */
export const amortizationDefaultsFrom = (disclosure) => {
  if (!disclosure) return { taxYearly: null, insuranceYearly: null, pmiMonthly: null };
  const mortgageInsurance = toMoney(disclosure.mortgage_insurance_annual);
  return {
    taxYearly: toMoney(disclosure.property_taxes_annual),
    insuranceYearly: toMoney(disclosure.homeowners_insurance_annual),
    pmiMonthly: mortgageInsurance != null ? roundCents(mortgageInsurance / 12) : null,
  };
};
